import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ProductionDeclarationStepKey =
  | 'guideline'
  | 'statement'
  | 'submission';

type ProductionDeclarationStepperProps = {
  currentStep: ProductionDeclarationStepKey;
  canNavigateToGuideline?: boolean;
  productionDeclarationId: string;
  token?: string;
};

const STEP_ORDER: ProductionDeclarationStepKey[] = [
  'guideline',
  'statement',
  'submission',
];

const STEP_LABEL_KEYS: Record<ProductionDeclarationStepKey, string> = {
  guideline: 'stepper.guideline',
  statement: 'stepper.statement',
  submission: 'stepper.submission',
};

export function ProductionDeclarationStepper({
  canNavigateToGuideline = false,
  currentStep,
  productionDeclarationId,
  token,
}: ProductionDeclarationStepperProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  return (
    <nav aria-label={t('stepper.ariaLabel')} className='w-full max-w-[420px]'>
      <ol className='flex items-start justify-end'>
        {STEP_ORDER.map((stepKey, index) => {
          const isActiveOrCompleted = index <= currentStepIndex;
          const isLast = index === STEP_ORDER.length - 1;
          const canNavigate = canNavigateToGuideline && stepKey === 'guideline';
          const stepClassName =
            'flex min-w-[92px] flex-col items-center gap-2 rounded-md text-center';
          const stepContent = (
            <>
              <span
                className={[
                  'flex size-7 items-center justify-center rounded-full border text-xs transition-colors',
                  isActiveOrCompleted
                    ? 'border-declaration-primary bg-declaration-primary text-white'
                    : 'border-declaration-muted bg-white text-declaration-muted-foreground',
                ].join(' ')}
              >
                {isActiveOrCompleted ? (
                  <Check className='size-3.5' />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={[
                  'font-medium text-xs leading-none',
                  isActiveOrCompleted
                    ? 'text-declaration-primary'
                    : 'text-declaration-foreground-muted',
                ].join(' ')}
              >
                {t(STEP_LABEL_KEYS[stepKey])}
              </span>
            </>
          );

          return (
            <li className='flex items-start' key={stepKey}>
              {canNavigate ? (
                <Link
                  aria-current={index === currentStepIndex ? 'step' : undefined}
                  className={[
                    stepClassName,
                    'transition-colors hover:text-declaration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-declaration-primary/30 focus-visible:ring-offset-4',
                  ].join(' ')}
                  params={{ id: productionDeclarationId }}
                  search={{ token }}
                  to='/production-declarations/$id/guideline'
                >
                  {stepContent}
                </Link>
              ) : (
                <div
                  aria-current={index === currentStepIndex ? 'step' : undefined}
                  className={stepClassName}
                >
                  {stepContent}
                </div>
              )}
              {isLast ? null : (
                <span
                  aria-hidden='true'
                  className='mt-3 block h-px w-10 bg-declaration-muted lg:w-12'
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
